// Mapping of [Officially assigned ISO-3166 alpha-2 codes](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)
// to official country names followed by common variants.

// At module load we build a reverse lookup (normalized variant → alpha-2) so
// runtime lookups are O(1). Bare alpha-2 codes (e.g. "US", "GB") are
// recognised directly via the COUNTRY_CODE_TO_LABELS keys, so there's no need to
// include each code in its own labels list.
const COUNTRY_CODE_TO_LABELS: Record<string, readonly string[]> = {
  AD: ['Andorra'],
  AE: ['United Arab Emirates', 'UAE'],
  AF: ['Afghanistan'],
  AG: ['Antigua and Barbuda'],
  AI: ['Anguilla'],
  AL: ['Albania'],
  AM: ['Armenia'],
  AO: ['Angola'],
  AQ: ['Antarctica'],
  AR: ['Argentina'],
  AS: ['American Samoa'],
  AT: ['Austria'],
  AU: ['Australia'],
  AW: ['Aruba'],
  AX: ['Åland Islands'],
  AZ: ['Azerbaijan'],
  BA: ['Bosnia and Herzegovina'],
  BB: ['Barbados'],
  BD: ['Bangladesh'],
  BE: ['Belgium'],
  BF: ['Burkina Faso'],
  BG: ['Bulgaria'],
  BH: ['Bahrain'],
  BI: ['Burundi'],
  BJ: ['Benin'],
  BL: ['Saint Barthélemy'],
  BM: ['Bermuda'],
  BN: ['Brunei Darussalam'],
  BO: ['Bolivia, Plurinational State of', 'Bolivia'],
  BQ: ['Bonaire, Sint Eustatius and Saba'],
  BR: ['Brazil'],
  BS: ['Bahamas'],
  BT: ['Bhutan'],
  BV: ['Bouvet Island'],
  BW: ['Botswana'],
  BY: ['Belarus'],
  BZ: ['Belize'],
  CA: ['Canada'],
  CC: ['Cocos (Keeling) Islands'],
  CD: [
    'Congo, Democratic Republic of the',
    'Congo, Democratic Republic',
    'Democratic Republic of the Congo',
    'Democratic Republic of Congo',
    'DR Congo',
    'DRC',
  ],
  CF: ['Central African Republic'],
  CG: ['Congo'],
  CH: ['Switzerland'],
  CI: ["Côte d'Ivoire"],
  CK: ['Cook Islands'],
  CL: ['Chile'],
  CM: ['Cameroon'],
  CN: ['China', "People's Republic of China", 'PRC'],
  CO: ['Colombia'],
  CR: ['Costa Rica'],
  CU: ['Cuba'],
  CV: ['Cabo Verde'],
  CW: ['Curaçao', 'Curacao'],
  CX: ['Christmas Island'],
  CY: ['Cyprus'],
  CZ: ['Czechia', 'Czech Republic'],
  DE: ['Germany'],
  DJ: ['Djibouti'],
  DK: ['Denmark'],
  DM: ['Dominica'],
  DO: ['Dominican Republic'],
  DZ: ['Algeria'],
  EC: ['Ecuador'],
  EE: ['Estonia'],
  EG: ['Egypt'],
  EH: ['Western Sahara'],
  ER: ['Eritrea'],
  ES: ['Spain'],
  ET: ['Ethiopia'],
  FI: ['Finland'],
  FJ: ['Fiji'],
  FK: ['Falkland Islands (Malvinas)', 'Falkland Islands', 'Islas Malvinas'],
  FM: ['Micronesia, Federated States of'],
  FO: ['Faroe Islands'],
  FR: ['France'],
  GA: ['Gabon'],
  GB: [
    'United Kingdom of Great Britain and Northern Ireland',
    'United Kingdom',
    'UK',
    'Great Britain',
    'Britain',
    'England',
    'Scotland',
    'Wales',
    'Northern Ireland',
  ],
  GD: ['Grenada'],
  GE: ['Georgia'],
  GF: ['French Guiana'],
  GG: ['Guernsey'],
  GH: ['Ghana'],
  GI: ['Gibraltar'],
  GL: ['Greenland'],
  GM: ['Gambia'],
  GN: ['Guinea'],
  GP: ['Guadeloupe'],
  GQ: ['Equatorial Guinea'],
  GR: ['Greece'],
  GS: ['South Georgia and the South Sandwich Islands', 'South Georgia'],
  GT: ['Guatemala'],
  GU: ['Guam'],
  GW: ['Guinea-Bissau'],
  GY: ['Guyana'],
  HK: ['Hong Kong'],
  HM: ['Heard Island and McDonald Islands', 'Heard Island', 'McDonald Island'],
  HN: ['Honduras'],
  HR: ['Croatia'],
  HT: ['Haiti'],
  HU: ['Hungary'],
  ID: ['Indonesia'],
  IE: ['Ireland', 'Republic of Ireland'],
  IL: ['Israel'],
  IM: ['Isle of Man'],
  IN: ['India'],
  IO: ['British Indian Ocean Territory'],
  IQ: ['Iraq'],
  IR: ['Iran, Islamic Republic of', 'Iran'],
  IS: ['Iceland'],
  IT: ['Italy'],
  JE: ['Jersey'],
  JM: ['Jamaica'],
  JO: ['Jordan'],
  JP: ['Japan'],
  KE: ['Kenya'],
  KG: ['Kyrgyzstan'],
  KH: ['Cambodia'],
  KI: ['Kiribati'],
  KM: ['Comoros'],
  KN: ['Saint Kitts and Nevis'],
  KP: ["Korea, Democratic People's Republic of", 'North Korea'],
  KR: ['Korea, Republic of', 'South Korea', 'Republic of Korea', 'Korea'],
  KW: ['Kuwait'],
  KY: ['Cayman Islands'],
  KZ: ['Kazakhstan'],
  LA: ["Lao People's Democratic Republic", 'Laos'],
  LB: ['Lebanon'],
  LC: ['Saint Lucia'],
  LI: ['Liechtenstein'],
  LK: ['Sri Lanka'],
  LR: ['Liberia'],
  LS: ['Lesotho'],
  LT: ['Lithuania'],
  LU: ['Luxembourg'],
  LV: ['Latvia'],
  LY: ['Libya'],
  MA: ['Morocco'],
  MC: ['Monaco'],
  MD: ['Moldova, Republic of', 'Moldova'],
  ME: ['Montenegro'],
  MF: ['Saint Martin (French part)'],
  MG: ['Madagascar'],
  MH: ['Marshall Islands'],
  MK: ['North Macedonia'],
  ML: ['Mali'],
  MM: ['Myanmar'],
  MN: ['Mongolia'],
  MO: ['Macao'],
  MP: ['Northern Mariana Islands'],
  MQ: ['Martinique'],
  MR: ['Mauritania'],
  MS: ['Montserrat'],
  MT: ['Malta'],
  MU: ['Mauritius'],
  MV: ['Maldives'],
  MW: ['Malawi'],
  MX: ['Mexico'],
  MY: ['Malaysia'],
  MZ: ['Mozambique'],
  NA: ['Namibia'],
  NC: ['New Caledonia'],
  NE: ['Niger'],
  NF: ['Norfolk Island'],
  NG: ['Nigeria'],
  NI: ['Nicaragua'],
  NL: ['Netherlands, Kingdom of the', 'Netherlands', 'Netherland'],
  NO: ['Norway'],
  NP: ['Nepal'],
  NR: ['Nauru'],
  NU: ['Niue'],
  NZ: ['New Zealand'],
  OM: ['Oman'],
  PA: ['Panama'],
  PE: ['Peru'],
  PF: ['French Polynesia'],
  PG: ['Papua New Guinea'],
  PH: ['Philippines'],
  PK: ['Pakistan'],
  PL: ['Poland'],
  PM: ['Saint Pierre and Miquelon'],
  PN: ['Pitcairn'],
  PR: ['Puerto Rico'],
  PS: ['Palestine, State of', 'Palestine'],
  PT: ['Portugal'],
  PW: ['Palau'],
  PY: ['Paraguay'],
  QA: ['Qatar'],
  RE: ['Réunion'],
  RO: ['Romania'],
  RS: ['Serbia'],
  RU: ['Russian Federation', 'Russia'],
  RW: ['Rwanda'],
  SA: ['Saudi Arabia'],
  SB: ['Solomon Islands'],
  SC: ['Seychelles'],
  SD: ['Sudan'],
  SE: ['Sweden'],
  SG: ['Singapore'],
  SH: ['Saint Helena, Ascension and Tristan da Cunha'],
  SI: ['Slovenia'],
  SJ: ['Svalbard and Jan Mayen'],
  SK: ['Slovakia'],
  SL: ['Sierra Leone'],
  SM: ['San Marino'],
  SN: ['Senegal'],
  SO: ['Somalia'],
  SR: ['Suriname'],
  SS: ['South Sudan'],
  ST: ['Sao Tome and Principe'],
  SV: ['El Salvador'],
  SX: ['Sint Maarten (Dutch part)'],
  SY: ['Syrian Arab Republic'],
  SZ: ['Eswatini'],
  TC: ['Turks and Caicos Islands'],
  TD: ['Chad'],
  TF: ['French Southern Territories'],
  TG: ['Togo'],
  TH: ['Thailand'],
  TJ: ['Tajikistan'],
  TK: ['Tokelau'],
  TL: ['Timor-Leste'],
  TM: ['Turkmenistan'],
  TN: ['Tunisia'],
  TO: ['Tonga'],
  TR: ['Türkiye', 'Turkey', 'Turkiye'],
  TT: ['Trinidad and Tobago'],
  TV: ['Tuvalu'],
  TW: ['Taiwan, Province of China', 'Taiwan'],
  TZ: ['Tanzania, United Republic of', 'Tanzania'],
  UA: ['Ukraine'],
  UG: ['Uganda'],
  UM: ['United States Minor Outlying Islands'],
  US: ['United States of America', 'United States', 'USA', 'America'],
  UY: ['Uruguay'],
  UZ: ['Uzbekistan'],
  VA: ['Holy See'],
  VC: ['Saint Vincent and the Grenadines'],
  VE: ['Venezuela, Bolivarian Republic of', 'Venezuela'],
  VG: ['Virgin Islands (British)'],
  VI: ['Virgin Islands (U.S.)'],
  VN: ['Viet Nam', 'Vietnam'],
  VU: ['Vanuatu'],
  WF: ['Wallis and Futuna'],
  WS: ['Samoa'],
  XK: ['Kosovo'],
  YE: ['Yemen'],
  YT: ['Mayotte'],
  ZA: ['South Africa'],
  ZM: ['Zambia'],
  ZW: ['Zimbabwe'],
};

const normalize = (name: string): string =>
  name
    .toLowerCase()
    .trim()
    .replace(/[.,'’]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/^the /, '');

const NAME_TO_COUNTRY_CODE: Record<string, string> = Object.entries(
  COUNTRY_CODE_TO_LABELS
).reduce<Record<string, string>>((acc, [code, labels]) => {
  labels.forEach((label) => {
    acc[normalize(label)] = code;
  });
  return acc;
}, {});

export const toCountryCode = (name: string): string | null => {
  if (!name) return null;
  const normalized = normalize(name);
  if (!normalized) return null;

  if (NAME_TO_COUNTRY_CODE[normalized]) return NAME_TO_COUNTRY_CODE[normalized];

  // Bare alpha-2 code (e.g. "US", "GB").
  const upper = normalized.toUpperCase();
  if (upper.length === 2 && upper in COUNTRY_CODE_TO_LABELS) return upper;

  return null;
};
